import { createDocument } from '@tiptap/core';
import { FIELD_TOKEN_DELETE_META } from './FieldTokenNode';

export function normalizeFieldKey(key) {
  const trimmed = String(key ?? '').trim();
  if (!trimmed) return '';
  return trimmed.toUpperCase();
}

export function formatKeyForInput(key) {
  const normalized = normalizeFieldKey(key);
  if (!normalized) return '{}';
  return `{${normalized}}`;
}

export function parseKeyFromInput(value) {
  const trimmed = String(value ?? '').trim();
  const match = trimmed.match(/^\{(.+)\}$/);
  const raw = match ? match[1].trim() : trimmed;
  return normalizeFieldKey(raw);
}

export const FIELD_KEY_MIN_LENGTH = 2;

export const FIELD_KEY_INPUT_ERROR =
  'Chave precisa ter no mínimo 2 caracteres e estar entre {}.';

/** Returns an error message while the user edits Chave, or null if valid/empty. */
export function getFieldKeyInputError(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;

  const match = trimmed.match(/^\{(.+)\}$/);
  if (!match) return FIELD_KEY_INPUT_ERROR;

  const inner = normalizeFieldKey(match[1]);
  if (inner.length < FIELD_KEY_MIN_LENGTH) return FIELD_KEY_INPUT_ERROR;

  return null;
}

export function isValidFieldKeyInput(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return false;
  return getFieldKeyInputError(trimmed) == null;
}

/** Validate all fields before save. Returns array of error messages. */
export function validateFieldsForSave(fields) {
  const errors = [];
  const list = fields ?? [];

  if (!list.length) {
    errors.push('Adicione pelo menos um campo.');
    return errors;
  }

  list.forEach((field) => {
    const label = field.key || 'sem chave';
    const keyDisplay = formatKeyForInput(field.key);

    if (getFieldKeyInputError(keyDisplay)) {
      errors.push(`${label}: ${FIELD_KEY_INPUT_ERROR}`);
    }

    if (!field.type) {
      errors.push(`${label}: informe o tipo.`);
    }

    if (!String(field.fieldName ?? '').trim()) {
      errors.push(`${label}: informe o nome do campo.`);
    }
  });

  const keyCounts = new Map();
  list.forEach((field) => {
    const normalized = normalizeFieldKey(field.key);
    if (!normalized) return;
    keyCounts.set(normalized, (keyCounts.get(normalized) ?? 0) + 1);
  });

  const duplicateKeys = [...keyCounts.entries()]
    .filter(([, count]) => count > 1)
    .map(([key]) => formatKeyForInput(key));

  if (duplicateKeys.length === 1) {
    errors.push(`Chave duplicada: ${duplicateKeys[0]}.`);
  } else if (duplicateKeys.length > 1) {
    errors.push(`Chaves duplicadas: ${duplicateKeys.join(', ')}.`);
  }

  return errors;
}

export function insertFieldToken(editor, { uuid, key }) {
  if (!editor) return false;
  return editor
    .chain()
    .focus()
    .insertContent({
      type: 'fieldToken',
      attrs: { uuid, key },
    })
    .run();
}

export function updateFieldTokenKeyByUuid(editor, uuid, newKey) {
  if (!editor || !uuid) return false;

  return editor.commands.command(({ tr, dispatch, state }) => {
    let changed = false;

    state.doc.descendants((node, pos) => {
      if (node.type.name === 'fieldToken' && node.attrs.uuid === uuid) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, key: newKey });
        changed = true;
      }
    });

    if (changed && dispatch) dispatch(tr);
    return changed;
  });
}

export function removeFieldTokensByUuid(editor, uuid) {
  if (!editor || !uuid) return false;

  const ranges = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'fieldToken' && node.attrs.uuid === uuid) {
      ranges.push({ from: pos, to: pos + node.nodeSize });
    }
  });

  if (!ranges.length) return true;

  return editor.commands.command(({ tr, dispatch }) => {
    tr.setMeta(FIELD_TOKEN_DELETE_META, true);
    ranges
      .sort((a, b) => b.from - a.from)
      .forEach(({ from, to }) => {
        tr.delete(from, to);
      });

    if (dispatch) dispatch(tr);
    return true;
  });
}

/** Inner token: min 2 chars, letters, digits, underscores (e.g. AB, ASSINATURA_VENDEDORES). */
export const BRACKET_TOKEN_INNER = '[A-Za-z0-9_]{2,}';
export const BRACKET_TOKEN_REGEX = new RegExp(
  `\\{\\s*(${BRACKET_TOKEN_INNER})\\s*\\}`,
  'g'
);

function normalizeTokenSearchChar(ch) {
  switch (ch) {
    case '\uFF5B':
    case '\u2774':
      return '{';
    case '\uFF5D':
    case '\u2775':
      return '}';
    default:
      if (/[\u200B-\u200D\uFEFF\u00AD]/.test(ch)) return null;
      return ch;
  }
}

function normalizeTokenSearchText(text) {
  let out = '';
  for (const ch of String(text ?? '')) {
    const normalized = normalizeTokenSearchChar(ch);
    if (normalized != null) out += normalized;
  }
  return out;
}

function extractKeysFromSearchText(text) {
  const keys = new Set();
  const normalized = normalizeTokenSearchText(text);
  const regex = new RegExp(BRACKET_TOKEN_REGEX.source, 'g');
  let match;

  while ((match = regex.exec(normalized)) !== null) {
    const key = normalizeFieldKey(match[1]);
    if (key) keys.add(key);
  }

  return keys;
}

/**
 * Build normalized searchable text for a textblock with doc-position mapping per character.
 */
function buildTextblockSearchIndex(blockNode, blockPos) {
  let searchText = '';
  const charStarts = [];
  const charEnds = [];

  blockNode.descendants((node, pos) => {
    if (node.type.name === 'fieldToken') return;

    if (node.isText) {
      for (let i = 0; i < node.text.length; i += 1) {
        const normalized = normalizeTokenSearchChar(node.text[i]);
        if (normalized == null) continue;
        searchText += normalized;
        charStarts.push(blockPos + 1 + pos + i);
        charEnds.push(blockPos + 1 + pos + i + 1);
      }
    }
  });

  return { searchText, charStarts, charEnds };
}

function findBracketTokensInTextblock(blockNode, blockPos) {
  const { searchText, charStarts, charEnds } = buildTextblockSearchIndex(
    blockNode,
    blockPos
  );
  const tokens = [];
  const regex = new RegExp(BRACKET_TOKEN_REGEX.source, 'g');
  let match;

  while ((match = regex.exec(searchText)) !== null) {
    const key = normalizeFieldKey(match[1]);
    if (!key) continue;

    const start = match.index;
    const end = match.index + match[0].length;
    const from = charStarts[start];
    const to = charEnds[end - 1];

    if (from == null || to == null) continue;
    tokens.push({ key, from, to });
  }

  return tokens;
}

function findBracketTokensInTextNode(node, pos) {
  let searchText = '';
  const charStarts = [];
  const charEnds = [];

  for (let i = 0; i < node.text.length; i += 1) {
    const normalized = normalizeTokenSearchChar(node.text[i]);
    if (normalized == null) continue;
    searchText += normalized;
    charStarts.push(pos + 1 + i);
    charEnds.push(pos + 2 + i);
  }

  const tokens = [];
  const regex = new RegExp(BRACKET_TOKEN_REGEX.source, 'g');
  let match;

  while ((match = regex.exec(searchText)) !== null) {
    const key = normalizeFieldKey(match[1]);
    if (!key) continue;

    const start = match.index;
    const end = match.index + match[0].length;
    const from = charStarts[start];
    const to = charEnds[end - 1];

    if (from == null || to == null) continue;
    tokens.push({ key, from, to });
  }

  return tokens;
}

function dedupeTokenRanges(tokens) {
  const seen = new Set();
  return tokens.filter(({ from, to, key }) => {
    const id = `${from}:${to}:${key}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function findAllBracketTokensInDoc(doc) {
  const tokens = [];

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return;
    tokens.push(...findBracketTokensInTextblock(node, pos));
  });

  if (tokens.length === 0) {
    doc.descendants((node, pos) => {
      if (!node.isText) return;
      tokens.push(...findBracketTokensInTextNode(node, pos));
    });
  }

  return dedupeTokenRanges(tokens);
}

export function convertTypedFieldTokenAtCursor(editor, ensureField) {
  if (!editor || typeof ensureField !== 'function') return false;

  const { state } = editor;
  const { $from } = state.selection;

  if (!$from.parent.isTextblock) return false;

  const textBefore = $from.parent.textBetween(
    0,
    $from.parentOffset,
    undefined,
    '\ufffc'
  );
  const normalizedBefore = normalizeTokenSearchText(textBefore);
  const match = normalizedBefore.match(
    new RegExp(`\\{\\s*(${BRACKET_TOKEN_INNER})\\s*\\}$`)
  );
  if (!match) return false;

  const key = normalizeFieldKey(match[1]);
  if (!key) return false;
  const field = ensureField(key);
  if (!field?.uuid) return false;

  const tokenLength = match[0].length;
  const from = $from.pos - tokenLength;
  const to = $from.pos;

  return editor
    .chain()
    .focus()
    .deleteRange({ from, to })
    .insertContentAt(from, {
      type: 'fieldToken',
      attrs: { uuid: field.uuid, key: field.key },
    })
    .run();
}

export function getNextCampoAdicionadoKey(fields) {
  const numbers = (fields ?? [])
    .map((field) => field.key.match(/^CAMPO_ADICIONADO_(\d+)$/)?.[1])
    .filter(Boolean)
    .map(Number);

  const next = numbers.length ? Math.max(...numbers) + 1 : 1;
  return `CAMPO_ADICIONADO_${next}`;
}

export function createTemplateField(key, type = 'text', fieldName = '') {
  const uuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `field-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const normalizedKey = normalizeFieldKey(key);
  const resolvedFieldName = String(fieldName ?? '').trim();

  return {
    uuid,
    key: normalizedKey,
    type,
    fieldName: resolvedFieldName,
    obrigatorio: false,
  };
}

/**
 * Replace the entire editor document, allowing protected fieldToken nodes to be removed.
 * TipTap clearContent/setContent is blocked by FieldTokenNode unless this meta is set.
 */
export function replaceAllEditorContent(editor, content, parseOptions = {}) {
  if (!editor) return false;

  return editor.commands.command(({ tr, dispatch, state }) => {
    tr.setMeta(FIELD_TOKEN_DELETE_META, true);

    const document = createDocument(content, editor.schema, parseOptions);
    tr.replaceWith(0, state.doc.content.size, document);

    if (dispatch) dispatch(tr);
    return true;
  });
}

/** Collect unique uppercase field keys from plain `{ABC}` text and existing tokens. */
export function collectBracketKeysFromEditor(editor) {
  if (!editor) return [];

  const keys = new Set();

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'fieldToken' && node.attrs.key) {
      keys.add(normalizeFieldKey(node.attrs.key));
    }
  });

  findAllBracketTokensInDoc(editor.state.doc).forEach(({ key }) => {
    keys.add(key);
  });

  extractKeysFromSearchText(editor.getText?.() ?? '').forEach((key) => keys.add(key));
  extractKeysFromSearchText(editor.getHTML?.() ?? '').forEach((key) => keys.add(key));

  return [...keys];
}

/** Replace plain `{KEY}` text (including across formatted runs) with fieldToken atoms. */
export function replaceBracketTokensInEditor(editor, keyToUuid) {
  if (!editor || !keyToUuid?.size) return 0;

  const matches = findAllBracketTokensInDoc(editor.state.doc).filter(({ key }) =>
    keyToUuid.has(key)
  );

  if (!matches.length) return 0;

  let replacedCount = 0;

  editor.commands.command(({ tr, state, dispatch }) => {
    const tokenType = state.schema.nodes.fieldToken;
    if (!tokenType) return false;

    matches
      .sort((a, b) => b.from - a.from)
      .forEach(({ key, from, to }) => {
        const uuid = keyToUuid.get(key);
        if (!uuid) return;
        tr.replaceWith(from, to, tokenType.create({ uuid, key }));
        replacedCount += 1;
      });

    if (dispatch) dispatch(tr);
    return replacedCount > 0;
  });

  return replacedCount;
}

/**
 * Register fields for bracket keys and convert plain tokens in the editor.
 * @param {import('@tiptap/core').Editor} editor
 * @param {(keys: string[]) => Map<string, string>} registerFieldsFromKeys — returns key → uuid
 */
export function syncBracketFieldsInEditor(editor, registerFieldsFromKeys) {
  if (!editor || typeof registerFieldsFromKeys !== 'function') {
    return { keys: [], replaced: 0 };
  }

  const keys = collectBracketKeysFromEditor(editor);
  if (!keys.length) {
    return { keys: [], replaced: 0 };
  }

  const keyToUuid = registerFieldsFromKeys(keys);
  const replaced = replaceBracketTokensInEditor(editor, keyToUuid);

  return { keys, replaced };
}

/** Run field sync after async editor updates (e.g. DOCX HTML import). */
export function syncBracketFieldsInEditorDeferred(editor, registerFieldsFromKeys) {
  if (!editor || typeof registerFieldsFromKeys !== 'function') {
    return { keys: [], replaced: 0 };
  }

  let latest = { keys: [], replaced: 0 };

  const resync = () => {
    const result = syncBracketFieldsInEditor(editor, registerFieldsFromKeys);
    if (result.keys.length >= latest.keys.length) {
      latest = result;
    }
  };

  resync();
  window.setTimeout(resync, 0);
  window.setTimeout(resync, 100);
  window.setTimeout(resync, 300);

  return latest;
}
